import React, { useCallback, useMemo, useRef } from "react";
import { Formik, FormikProps } from "formik";
import {
  AsyncSelectPickerInput,
  Button,
  DatePickerInput,
  ErrorMessageField,
  Icon,
  SelectPickerInput,
} from "@shared/components";
import {
  CreateSensorDataRestoringRequesFormShape,
  CreateSensorDataRestoringRequestRequest,
} from "@containers/SensorDataRestoringRequest/interfaces";
import { Option } from "@shared/interfaces";
import { Hospital } from "@shared/models";
import { DATE_FORMAT } from "@shared/constants";
import { getTimezoneList } from "@shared/utils";

import { getInitValues, validationSchema, getHandlers, prepareSubmitValue } from "./formHelpers";
import "./index.scss";

interface SensorDataRestoringRequestFormProps {
  hospital: Hospital;
  onChangeForm: () => void;
  onCloseForm: () => void;
  onSubmitForm: (payload: CreateSensorDataRestoringRequestRequest) => void;
}

const SensorDataRestoringRequestForm: React.FunctionComponent<SensorDataRestoringRequestFormProps> = (props) => {
  const { hospital, onChangeForm, onCloseForm, onSubmitForm } = props;

  const formikRef = useRef<FormikProps<CreateSensorDataRestoringRequesFormShape>>(null);

  const timezoneOptions = useMemo(() => getTimezoneList(), []);
  const formValues = useMemo(() => getInitValues(), []);
  const handlers = useMemo(
    () =>
      getHandlers({
        label: hospital.name,
        value: String(hospital.id),
      }),
    [hospital],
  );

  const onChangeSelect = useCallback((field: string, value: Option | null) => {
    if (formikRef.current) {
      formikRef.current.setFieldValue(field, value);
    }
  }, []);

  const onChangeStation = useCallback(
    (value: Option | null) => {
      onChangeSelect("station", value);
    },
    [onChangeSelect],
  );

  return (
    <Formik
      innerRef={formikRef}
      validationSchema={validationSchema}
      enableReinitialize={true}
      validateOnBlur={false}
      onSubmit={(values, { setSubmitting }) => {
        onSubmitForm(prepareSubmitValue(values, hospital));
        setSubmitting(false);
      }}
      initialValues={formValues}
      validate={() => {
        onChangeForm();
      }}
    >
      {({ values, handleSubmit, handleChange, setFieldValue }) => (
        <div className="request-form">
          <form onSubmit={handleSubmit}>
            <div className="request-form-header">
              <div className="request-form-header-left">New Restore Data</div>
              <div className="request-form-header-right">
                <Button type="submit" className="action-btn">
                  Apply
                </Button>
                <Button className="close-btn" onClick={() => onCloseForm()}>
                  <Icon type="clear" />
                </Button>
              </div>
            </div>
            <div className="request-form-content">
              <div className="station-select">
                <AsyncSelectPickerInput
                  labelTitle="Station*"
                  name="station"
                  className="station-input"
                  onChange={onChangeStation}
                  value={values.station}
                  placeholder="Select Station"
                  getData={handlers.station.getData}
                  selectData={handlers.station.selectData}
                  prepareOptionFunction={handlers.station.prepareOptionFunction}
                  loadingAction={handlers.station.loadingAction}
                />
                <ErrorMessageField name="station" />
              </div>
              <div className="request-date-range">
                <div className="request-date-range-item">
                  <DatePickerInput
                    placement="auto"
                    labelTitle="From*"
                    onChange={(e) => setFieldValue("start_date", e)}
                    name="start_date"
                    value={values.start_date}
                    format={DATE_FORMAT.DATE_PICKER}
                  />
                  <ErrorMessageField name="start_date" />
                </div>
                <div className="request-date-range-item">
                  <DatePickerInput
                    placement="auto"
                    labelTitle="To*"
                    onChange={(e) => setFieldValue("end_date", e)}
                    name="end_date"
                    value={values.end_date}
                    format={DATE_FORMAT.DATE_PICKER}
                  />
                  <ErrorMessageField name="end_date" />
                </div>
              </div>
              <div className="station-select">
                <SelectPickerInput
                  searchable
                  name="timezone"
                  data={timezoneOptions}
                  onChange={(value) => setFieldValue("timezone", value)}
                  onClean={handleChange}
                  labelTitle="Time Zone"
                  value={values.timezone}
                  placeholder="Time Zone"
                  placement="auto"
                />
                <ErrorMessageField name="timezone" />
              </div>
            </div>
          </form>
        </div>
      )}
    </Formik>
  );
};

export default SensorDataRestoringRequestForm;
